import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Box, Typography, RadioGroup, FormControlLabel, Radio, Divider, Chip, Paper, Switch, useMediaQuery } from '@mui/material';
import { Eye } from 'lucide-react';
import Dropdown from '../ui/dropdown';
import GeneralProvisionIncludedPanel, { IncludedTransactionRow } from './parts/general-provision-included-panel';
import { SlideUpTransition } from './mobile/mobile-bottom-sheet';
import TreeDropdown, { TreeDropdownOption } from '../tree-dropdown';
import CalendarInput from '../ui/calendar-input';
import useApi from '../../hooks/use-api';
import buildApiRequestConfig from '../../api/url-builder';
import routes from '../../api/routes';
import { useUser } from '../../providers/user-provider';
import { useToast } from '../../providers/toaster-provider';
import { useSettings } from '../../providers/settings-provider';
import theme from '../../util/theme/theme';

export type GeneralProvisionKind = 'income' | 'expense';
export type GeneralProvisionPeriod = 'daily' | 'weekly' | 'monthly';

export type GeneralProvision = {
    id: number | null;
    kind: GeneralProvisionKind;
    // Disabled (or never saved) = no provision rows on the Transactions page.
    enabled: boolean;
    // The dates the included transactions come from (YYYY-MM-DD).
    consideredFrom: string;
    consideredTo: string;
    description: string;
    period: GeneralProvisionPeriod;
    lookbackDays: number;
    startDate: string | null;
    categoryId: number | null;
    // 'ai' while the AI is still choosing the category, 'manual' once someone has.
    categorySource: 'ai' | 'manual' | null;
    bankAccountId: number | null;
    // Transactions marked as outliers - left out of the amount.
    excludedTransactionGuids: string[];
    endDate: null;
    amount: number;
    includedTotal: number;
    includedCount: number;
    excludedCount: number;
};

type IncludedTransaction = IncludedTransactionRow & { categoryName: string | null; excluded: boolean };

// Days in one period - same as general-provision-controller.js, so the
// amount previewed here while ticking outliers matches what's saved.
const PERIOD_DAYS: Record<GeneralProvisionPeriod, number> = { daily: 1, weekly: 7, monthly: 365.25 / 12 };

const PERIOD_OPTIONS: { value: GeneralProvisionPeriod; label: string }[] = [
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
];
const LOOKBACK_PRESETS = [30, 60, 90, 180, 365];
const ANY_BANK = '__any__';
const PERIOD_NOUN: Record<GeneralProvisionPeriod, string> = { daily: 'day', weekly: 'week', monthly: 'month' };

const labelSx = (isMobile: boolean) => ({ fontWeight: 500, fontSize: isMobile ? 12 : 15, mb: isMobile ? 0.25 : 0.5, color: 'var(--color-text-muted)' });

type Props = {
    open: boolean;
    provision: GeneralProvision | null;
    categoryOptions: TreeDropdownOption[];
    onClose: () => void;
    onSaved: () => void;
};

// Edit modal for a General Income / General Expense provision - laid out
// like Edit Provision, with the recurrence settings replaced by the
// transactions it's built from: the company's real transactions not linked
// to any provision, going back `lookbackDays`. Its amount is those
// transactions' total spread over the chosen period, so the description,
// amount and end date are shown but not editable.
const GeneralProvisionModal: React.FC<Props> = ({ open, provision, categoryOptions, onClose, onSaved }) => {
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { user } = useUser();
    const { showSuccess, showError } = useToast();
    const { formatAmount } = useSettings();
    const fmtAmount = (n: number) => formatAmount?.(n) ?? `$${n.toFixed(2)}`;

    const [period, setPeriod] = React.useState<GeneralProvisionPeriod>('weekly');
    const [lookbackDays, setLookbackDays] = React.useState<number>(90);
    const [customLookback, setCustomLookback] = React.useState<string>('');
    const [startDate, setStartDate] = React.useState<string>('');
    const [category, setCategory] = React.useState<string>('');
    const [bank, setBank] = React.useState<string>(ANY_BANK);
    const [bankOptions, setBankOptions] = React.useState<{ value: string; label: string }[]>([]);
    // Included Transactions panel - open by default, unlike Edit Provision's
    // Occurrences, since it's what this provision's amount is made of.
    const [showPanel, setShowPanel] = React.useState(true);
    const [enabled, setEnabled] = React.useState(true);
    const [excludedGuids, setExcludedGuids] = React.useState<Set<string>>(new Set());
    const toggleExcluded = (guid: string) => setExcludedGuids(prev => {
        const next = new Set(prev);
        if (next.has(guid)) next.delete(guid); else next.add(guid);
        return next;
    });

    const [fetchBanks, setFetchBanks] = useApi<any[]>();
    const [fetchIncluded, setFetchIncluded] = useApi<{ data: GeneralProvision & { transactions: IncludedTransaction[] } }>();
    const [save, setSave] = useApi<{ data: GeneralProvision }>();

    React.useEffect(() => {
        if (!open || !provision) return;
        setPeriod(provision.period);
        setLookbackDays(provision.lookbackDays);
        setCustomLookback(LOOKBACK_PRESETS.includes(provision.lookbackDays) ? '' : String(provision.lookbackDays));
        setStartDate(provision.startDate || '');
        setCategory(provision.categoryId != null ? String(provision.categoryId) : '');
        setBank(provision.bankAccountId != null ? String(provision.bankAccountId) : ANY_BANK);
        setShowPanel(true);
        // A never-saved one opens switched on - saving it is how it gets turned on.
        setEnabled(provision.id == null ? true : provision.enabled);
        setExcludedGuids(new Set(provision.excludedTransactionGuids || []));
        setFetchBanks(buildApiRequestConfig(routes.bankAccounts.fetchByEntity, [{ param: 'entityId', value: user?.entityId }]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, provision]);

    React.useEffect(() => {
        const { data, isLoading } = fetchBanks;
        if (data && !isLoading) {
            setBankOptions([
                { value: ANY_BANK, label: 'Any Bank Account' },
                ...data.map((item: any) => ({ value: String(item.id), label: `${item.bank} (${item.name})` })),
            ]);
        }
    }, [fetchBanks]);

    // Re-reads the included transactions (and the amount they make) whenever
    // the window, period or bank account changes, before anything is saved.
    React.useEffect(() => {
        if (!open || !provision || !(lookbackDays >= 1)) return;
        const handle = setTimeout(() => {
            const { url, method } = buildApiRequestConfig(routes.generalProvisions.fetchTransactions, [
                { param: 'entityId', value: user?.entityId },
                { param: 'kind', value: provision.kind },
            ]);
            const query = new URLSearchParams({
                lookbackDays: String(lookbackDays),
                period,
                bankAccountId: bank === ANY_BANK ? '' : bank,
            });
            setFetchIncluded({ url: `${url}?${query.toString()}`, method });
        }, 300);
        return () => clearTimeout(handle);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, provision, lookbackDays, period, bank]);

    React.useEffect(() => {
        const { data, isLoading, error } = save;
        if (data && !isLoading) {
            showSuccess(`${provision?.description || 'Provision'} saved`);
            onSaved();
            onClose();
        }
        if (error) showError(typeof error === 'string' ? error : 'Failed to save');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [save]);

    if (!provision) return null;

    const preview = fetchIncluded.data?.data;
    const transactions = preview?.transactions ?? [];
    // Recomputed here rather than taken from the server, so ticking an
    // outlier updates the amount immediately - the server's own figure uses
    // the saved outliers, not the ones being edited.
    const counted = transactions.filter(t => !excludedGuids.has(t.guid));
    const countedTotal = Math.round(counted.reduce((sum, t) => sum + Number(t.amount), 0) * 100) / 100;
    const periods = lookbackDays / PERIOD_DAYS[period];
    const amount = preview ? (periods > 0 ? Math.round((countedTotal / periods) * 100) / 100 : 0) : provision.amount;
    const outlierCount = transactions.length - counted.length;
    const customActive = customLookback !== '';

    const handleSave = () => {
        const { url, method } = buildApiRequestConfig(routes.generalProvisions.update, [
            { param: 'entityId', value: user?.entityId },
            { param: 'kind', value: provision.kind },
        ]);
        setSave({
            url,
            method,
            body: {
                enabled,
                period,
                lookbackDays,
                categoryId: category ? Number(category) : null,
                bankAccountId: bank === ANY_BANK ? null : Number(bank),
                excludedTransactionGuids: Array.from(excludedGuids),
            },
        });
    };

    const kindNoun = provision.kind === 'income' ? 'income' : 'expenses';
    const panel = (
        <GeneralProvisionIncludedPanel
            transactions={transactions}
            excludedGuids={excludedGuids}
            onToggleExcluded={toggleExcluded}
            isLoading={fetchIncluded.isLoading}
            emptyMessage={`No unlinked ${kindNoun} in the ${lookbackDays} days up to the start date`}
            onClose={() => setShowPanel(false)}
        />
    );

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth={isMobile}
            maxWidth={false}
            {...(isMobile ? { TransitionComponent: SlideUpTransition } : {})}
            sx={isMobile ? { '& .MuiDialog-container': { alignItems: 'flex-end' } } : undefined}
            PaperProps={{
                // Same two-card layout as Edit Provision (add-provision.tsx):
                // the form card drives the height, and the Included
                // Transactions card beside it fills that height.
                sx: isMobile
                    ? { m: 0, width: '100%', maxWidth: '100%', background: 'transparent', boxShadow: 'none', display: 'flex', flexDirection: 'column', gap: 1, maxHeight: '90vh' }
                    : { background: 'transparent', boxShadow: 'none', display: 'flex', flexDirection: 'row', alignItems: 'stretch', gap: 0.25, margin: 'auto', maxWidth: 'none', maxHeight: '90vh' },
            }}
        >
            <Paper sx={{
                borderRadius: isMobile ? '16px 16px 0 0' : (showPanel ? '12px 0 0 12px' : 3),
                minWidth: isMobile ? undefined : 650,
                width: isMobile ? '100%' : 650,
                p: isMobile ? 0.25 : 0.75,
                maxHeight: isMobile ? (showPanel ? '55vh' : '90vh') : '90vh',
                overflowY: 'auto',
            }}>
            {/* Wraps on a phone: the title and the Included Transactions
                button stay on the first line, the Enabled switch drops below. */}
            <DialogTitle sx={{ fontWeight: 700, fontSize: isMobile ? 16 : 22, pb: isMobile ? 0.5 : 1, display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 0.5, rowGap: 0.25 }}>
                <span>Edit {provision.description}</span>
                <Button
                    size="small"
                    startIcon={<Eye size={16} />}
                    onClick={() => setShowPanel(v => !v)}
                    sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: 13,
                        borderRadius: 2,
                        px: 1.25,
                        py: 0.25,
                        minWidth: 0,
                        color: showPanel ? 'var(--color-primary)' : 'var(--color-text-muted)',
                        background: showPanel ? 'rgba(var(--color-primary-rgb), 0.1255)' : 'var(--color-container-background)',
                        '&:hover': { background: showPanel ? 'rgba(var(--color-primary-rgb), 0.251)' : 'var(--color-container-background)' },
                    }}
                >
                    Included Transactions
                </Button>
                <FormControlLabel
                    sx={{ ml: "auto", mr: 0 }}
                    control={<Switch checked={enabled} onChange={e => setEnabled(e.target.checked)} sx={{ "& .Mui-checked": { color: "var(--color-primary)" }, "& .Mui-checked + .MuiSwitch-track": { backgroundColor: "var(--color-primary)" } }} />}
                    label={<Typography sx={{ fontSize: 14, fontWeight: 600, color: enabled ? "var(--color-primary)" : "var(--color-text-muted)" }}>{enabled ? "Enabled" : "Disabled"}</Typography>}
                />
            </DialogTitle>
            <DialogContent sx={{ pt: isMobile ? 0.5 : 1, px: isMobile ? 1 : 2 }}>
                <Box display="flex" gap={isMobile ? 0.5 : 2} mb={isMobile ? 1 : 2} flexDirection={isMobile ? 'column' : 'row'}>
                    <Box flex={0.45} position="relative">
                        <Typography sx={labelSx(isMobile)}>Transaction Amount</Typography>
                        <TextField fullWidth size="small" value={fmtAmount(amount)} disabled sx={{ background: 'var(--color-page-background)', borderRadius: 2 }} />
                        <Typography variant="caption" color="textSecondary" sx={{ position: 'absolute', bottom: -22, left: 0, width: isMobile ? 'auto' : 500 }}>
                            The included transactions' total (outliers excluded), averaged per {PERIOD_NOUN[period]}.
                        </Typography>
                    </Box>
                    <Box flex={1} sx={isMobile ? { mt: 2 } : {}}>
                        <Typography sx={labelSx(isMobile)}>Description</Typography>
                        <TextField fullWidth size="small" value={provision.description} disabled sx={{ background: 'var(--color-page-background)', borderRadius: 2 }} />
                    </Box>
                </Box>
                <Divider sx={{ mt: 3.5, mb: 1.5 }} />

                <Box display="flex" gap={2} mb={1.5} flexDirection={isMobile ? 'column' : 'row'}>
                    <Box sx={{ minWidth: 140, borderRight: isMobile ? 'none' : '1px solid #e0e0e0', pr: 2 }}>
                        <Typography sx={{ fontWeight: 500, fontSize: 14, mb: 0.5 }}>Period</Typography>
                        <RadioGroup value={period} onChange={e => setPeriod(e.target.value as GeneralProvisionPeriod)}>
                            {PERIOD_OPTIONS.map(o => (
                                <FormControlLabel key={o.value} value={o.value} label={<Typography fontSize={14}>{o.label}</Typography>} control={<Radio size="small" sx={{ color: 'var(--color-primary)' }} />} />
                            ))}
                        </RadioGroup>
                    </Box>
                    <Box flex={1}>
                        <Typography sx={{ fontWeight: 500, fontSize: 14, mb: 0.5 }}>Start Date</Typography>
                        {/* Always today - the window and the rows move with the date. */}
                        <CalendarInput value={startDate} onChange={() => {}} disabled />
                        {/* Shown for parity with Edit Provision - a general
                            provision always runs with no end date. */}
                        <RadioGroup row value="none" sx={{ mt: 1.5, justifyContent: 'space-between' }}>
                            <FormControlLabel disabled value="date" label={<Typography fontSize={14}>End by</Typography>} control={<Radio size="small" />} />
                            <FormControlLabel disabled value="count" label={<Typography fontSize={14}>End after</Typography>} control={<Radio size="small" />} />
                            <FormControlLabel disabled value="none" label={<Typography fontSize={14}>No end date</Typography>} control={<Radio size="small" />} />
                        </RadioGroup>
                    </Box>
                </Box>
                <Divider sx={{ mb: 1.5 }} />

                <Box mb={isMobile ? 1 : 2}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} mb={1}>
                        <Typography sx={labelSx(isMobile)}>Include transactions from the days before the start date</Typography>
                        <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
                            {fetchIncluded.isLoading ? 'Loading...' : `${counted.length} unlinked ${provision.kind === 'income' ? 'income' : 'expense'} transaction${counted.length === 1 ? '' : 's'} · ${fmtAmount(countedTotal)}${outlierCount ? ` (${outlierCount} outlier${outlierCount === 1 ? '' : 's'} excluded)` : ''}`}
                        </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={0.75} flexWrap="wrap">
                        {LOOKBACK_PRESETS.map(days => (
                            <Chip
                                key={days}
                                size="small"
                                label={`${days} days`}
                                onClick={() => { setCustomLookback(''); setLookbackDays(days); }}
                                sx={{
                                    fontWeight: 600,
                                    color: !customActive && lookbackDays === days ? '#fff' : 'var(--color-text-muted)',
                                    background: !customActive && lookbackDays === days ? 'var(--color-primary)' : 'var(--color-container-background)',
                                    '&:hover': { background: !customActive && lookbackDays === days ? 'var(--color-primary)' : 'var(--color-container-background)' },
                                }}
                            />
                        ))}
                        <TextField
                            size="small"
                            placeholder="Custom"
                            value={customLookback}
                            onChange={e => {
                                const digits = e.target.value.replace(/\D/g, '').slice(0, 4);
                                setCustomLookback(digits);
                                if (digits) setLookbackDays(Number(digits));
                            }}
                            InputProps={{ endAdornment: <Typography fontSize={12} color="var(--color-text-muted)">days</Typography> }}
                            sx={{ width: 120, '& .MuiInputBase-root': { height: 26, fontSize: 13, borderRadius: 4 }, ...(customActive ? { '& fieldset': { borderColor: 'var(--color-primary)' } } : {}) }}
                        />
                    </Box>
                </Box>
                <Divider sx={{ mb: 1.5 }} />

                <Box mb={isMobile ? 1 : 2}>
                    <Typography sx={labelSx(isMobile)}>Category</Typography>
                    <TreeDropdown options={categoryOptions} value={category} onChange={(id) => setCategory(id || '')} placeholder="Choose Category" height={40} width="100%" />
                    {/* Still the AI's pick (unchanged here) - choosing another one takes it over for good. */}
                    {provision.categorySource === 'ai' && provision.categoryId != null && category === String(provision.categoryId) && (
                        <Typography variant="caption" color="textSecondary">
                            Picked by AI from your categories. Choose a different one to set it yourself.
                        </Typography>
                    )}
                </Box>
                <Box mb={isMobile ? 1 : 2}>
                    <Typography sx={labelSx(isMobile)}>Bank Account</Typography>
                    <Dropdown options={bankOptions} value={bank} onChange={(val) => setBank(val || ANY_BANK)} placeholder="Choose Bank Account" height={40} />
                </Box>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'space-between', px: isMobile ? 1 : 3, py: isMobile ? 0.75 : 2 }}>
                <Button onClick={onClose} variant="contained" sx={{ background: 'var(--color-container-background)', color: 'var(--color-text-muted)', boxShadow: 'none', borderRadius: 2, fontWeight: 600, width: 120, '&:hover': { background: 'var(--color-container-background)' } }}>
                    Cancel
                </Button>
                <Button onClick={handleSave} disabled={save.isLoading || !(lookbackDays >= 1)} variant="contained" sx={{ background: 'var(--color-primary)', color: '#fff', boxShadow: 'none', borderRadius: 2, fontWeight: 600, width: 160, '&:hover': { background: 'var(--color-primary)' }, '&:disabled': { opacity: 0.6, background: 'var(--color-primary)', color: '#fff' } }}>
                    {save.isLoading ? 'Saving...' : 'Save Changes'}
                </Button>
            </DialogActions>
            </Paper>
            {showPanel && (isMobile ? (
                <Paper sx={{ borderRadius: '0 0 16px 16px', width: '100%', flexShrink: 0, maxHeight: '35vh', display: 'flex', overflow: 'hidden' }}>
                    {panel}
                </Paper>
            ) : (
                // Empty positioning spacer - stretched to the form card's
                // height, with the panel card filling it (see add-provision.tsx).
                <Box sx={{ position: 'relative', width: 360, flexShrink: 0 }}>
                    <Paper sx={{ borderRadius: '0 12px 12px 0', position: 'absolute', inset: 0, display: 'flex', overflow: 'hidden' }}>
                        {panel}
                    </Paper>
                </Box>
            ))}
        </Dialog>
    );
};

export default GeneralProvisionModal;
